import { useTranslation } from 'react-i18next';
import { Info, Lock, Users } from 'lucide-react';
import { cn } from 'lib/utils';
import { Skeleton } from 'components/ui/skeleton';
import { initials } from 'components/rating/RatingForm';
import { formatDateTime } from 'utils/formatDate';
import { apiErrorMessage } from 'utils/errorMessage';
import type {
  InterventionRequest,
  MissionParticipant,
  ReminderSettings,
} from 'api/interventionRequestsApi';
import { reminderNote, technicianName } from 'pages/AdminIssues/components/upcomingAssignment';
import RemindButton from 'pages/AdminIssues/components/RemindButton';
import { remindHint, remindState, rowPhrase, rowStatusLabel } from 'pages/AdminIssues/components/solicitationRows';
import { retainedTechnician, statusDot, statusTone } from '../vocabulary';
import { DRAWER_BADGE, DrawerRow, DrawerSection } from './DrawerSection';

/**
 * Every technician solicited on the mission, and which of them ended up doing it — the heart of
 * L5b, since several can now be asked at once and only one may accept (D47).
 *
 * One action only, « Relancer » (L6, E6), for who may manage technicians; the rows read like the
 * ticket screen's. Soliciting or cancelling stays the ticket screen's business, and the card says so.
 */

interface MissionSolicitationsProps {
  /** Every request of the mission, newest first. */
  requests: InterventionRequest[];
  /** The Jira access list, read to be reported and never acted upon (D50). */
  participants: MissionParticipant[];
  /** The request the drawer was opened on — the one line badged « Cette demande ». */
  openedRequestId: number;
  isLoading: boolean;
  error: unknown;
  /** Absent for a view-only reader: no « Relancer » at all rather than a button that answers 403. */
  onRemind?: (request: InterventionRequest) => void;
  /** The request whose reminder is in flight — its button spins and every « Relancer » waits. */
  remindingId?: number | null;
  remindError?: unknown;
  /** The desk saves the deadline from the ticket screen — decides the deadline-passed hint. */
  deadlineEditable?: boolean;
  /** The backend's reminder settings; absent until the mission read lands. */
  reminderSettings?: ReminderSettings;
}

export const MissionSolicitations = ({
  requests,
  participants,
  openedRequestId,
  isLoading,
  error,
  onRemind,
  remindingId = null,
  remindError = null,
  deadlineEditable = false,
  reminderSettings,
}: MissionSolicitationsProps) => {
  const { t } = useTranslation();
  const retained = retainedTechnician(t, requests);
  const acceptor = requests.find((request) => request.status === 'accepted') ?? null;
  const now = Date.now();
  /*
   * Technicians of ours that Jira carries on the ticket without a GPS request behind them (D50).
   * Said, and nothing more: there is no « solliciter cette personne » here, deliberately — Jira's
   * list is managed in Jira.
   */
  const observed = participants.filter(
    (participant) => participant.technicianUserId !== null && !participant.solicited
  );

  return (
    <DrawerSection
      icon={Users}
      title={t('Technicians solicited on the mission')}
      aside={
        requests.length > 0 && (
          <span className="inline-flex h-[21px] items-center rounded-[6px] bg-muted px-2 text-[11.5px] font-semibold text-muted-foreground">
            {t('{{count}} solicited', { count: requests.length })}
          </span>
        )
      }
      bodyClassName="flex flex-col"
    >
      {isLoading ? (
        <div className="flex flex-col gap-2 p-4">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-2/3" />
        </div>
      ) : error ? (
        // Its own failure, and only its own: the rest of the drawer came with the row.
        <p role="alert" className="p-4 text-[12.5px] text-red-600 dark:text-red-400">
          {apiErrorMessage(t, error)}
        </p>
      ) : (
        <ul>
          {/* Chronological, oldest first: a list grouped by status hides the order in which the
              mission actually went round, which is what the reader came for. */}
          {[...requests].reverse().map((request) => {
            const remind = request.status === 'pending' ? remindState(request, now) : null;
            const hint = remindHint(t, request, remind, !!onRemind, deadlineEditable);
            return (
              <li
                key={request.id}
                className={cn(
                  'flex items-start gap-[11px] border-b px-4 py-[11px]',
                  request.id === openedRequestId && 'bg-muted/60'
                )}
              >
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-[11.5px] font-semibold text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                  aria-hidden="true"
                >
                  {initials(technicianName(request))}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-[2px] leading-[1.35]">
                  <span className="flex min-w-0 flex-wrap items-center gap-[7px]">
                    <span className="truncate text-[13px] font-semibold">
                      {technicianName(request)}
                    </span>
                    {request.id === openedRequestId && (
                      <span className="inline-flex h-[19px] shrink-0 items-center rounded-[6px] border border-input px-[6px] text-[11px] font-medium text-muted-foreground">
                        {t('This request')}
                      </span>
                    )}
                  </span>
                  <span className="text-[12px] text-muted-foreground">
                    {rowPhrase(t, request, acceptor)}
                  </span>
                  {hint && <span className={cn('text-[12px]', hint.tone)}>{hint.text}</span>}
                </div>
                <div className="ml-auto flex shrink-0 flex-col items-end gap-1.5">
                  <span className={cn(DRAWER_BADGE, statusTone(request.status))}>
                    <span
                      className={cn('h-1.5 w-1.5 rounded-full', statusDot(request.status))}
                      aria-hidden="true"
                    />
                    {rowStatusLabel(t, request, remind)}
                  </span>
                  {onRemind && (remind === 'open' || remind === 'cooling') && (
                    <RemindButton
                      cooling={remind === 'cooling'}
                      remindingId={remindingId}
                      requestId={request.id}
                      onClick={() => onRemind(request)}
                    />
                  )}
                </div>
              </li>
            );
          })}
          <li className="flex flex-col gap-[7px] px-4 pb-[13px]">
            <DrawerRow label={t('Retained technician')} value={retained.label} />
            {observed.map((participant) => (
              <span
                key={participant.accountId}
                className="inline-flex items-start gap-[7px] text-[11.5px] leading-[1.5] text-muted-foreground"
              >
                <Info className="mt-[2px] h-[13px] w-[13px] shrink-0" aria-hidden="true" />
                {participant.addedByName && participant.addedAt
                  ? t(
                      'Jira also carries {{name}} on the ticket, added by {{author}} on {{date}}, with no GPS request.',
                      {
                        name: participant.displayName,
                        author: participant.addedByName,
                        date: formatDateTime(participant.addedAt),
                      }
                    )
                  : t('Jira also carries {{name}} on the ticket, with no GPS request.', {
                      name: participant.displayName,
                    })}
              </span>
            ))}
            {onRemind && reminderSettings && requests.some((request) => request.status === 'pending') && (
              <span className="text-[11.5px] leading-[1.5] text-muted-foreground/70">
                {reminderNote(t, reminderSettings)}
              </span>
            )}
            {remindError ? (
              <span role="alert" className="text-[12.5px] text-rose-600 dark:text-rose-400">
                {apiErrorMessage(t, remindError)}
              </span>
            ) : null}
            <span className="inline-flex items-start gap-[7px] text-[11.5px] leading-[1.5] text-muted-foreground/70">
              <Lock className="mt-[2px] h-[13px] w-[13px] shrink-0" aria-hidden="true" />
              {onRemind
                ? t('Solicitations are made from the Tickets screen.')
                : t('Consultation only — soliciting or cancelling a request is done on the Tickets screen.')}
            </span>
          </li>
        </ul>
      )}
    </DrawerSection>
  );
};
